import { Injectable } from '@angular/core';
import { SeoAudit } from "../shared/models/seo-audits/seo-audit";
import { SeoAuditSummary } from "../shared/models/seo-audits-summary/seo-audit-summary";
import { Statuses } from "../shared/models/seo-audits-summary/statuses";

@Injectable({
  providedIn: 'root'
})
export class SeoAuditSummaryFactory {
  public Create(report: SeoAudit): SeoAuditSummary {
    const data = report.ReportData;
    return {
      aiUserAgentsAllowed: {
        title: 'AI User Agents',
        content: this.getContent([data.AIReadiness?.AIUserAgentsAllowed], () => data.AIReadiness?.AIUserAgentsAllowed ? 'Allowed' : 'Not Allowed'),
        status: this.getStatus([data.AIReadiness?.AIUserAgentsAllowed], () => data.AIReadiness?.AIUserAgentsAllowed ? Statuses.Pass : Statuses.Fail)
      },
      amountOfContent: {
        title: 'Amount of Content',
        content: this.getContent([data.AmountOfContent?.TotalWordCount, data.AmountOfContent?.PagesTested], () => `${data.AmountOfContent.TotalWordCount} words on ${data.AmountOfContent.PagesTested} pages`),
        status: this.getStatus([data.AmountOfContent?.TotalWordCount, data.AmountOfContent?.PagesTested], () => Statuses.Info),
      },
      backlinks: {
        title: 'Backlinks',
        content: this.getContent([data.Backlinks?.TotalBacklinks], () => `${data.Backlinks.TotalBacklinks} backlinks`),
        status: this.getStatus([data.Backlinks?.TotalBacklinks], () => Statuses.Info)
      },
      brokenLinks: {
        title: 'Broken Links',
        content: this.getContent([data.BrokenLinks?.LinksBrokenCount, data.BrokenLinks?.LinksAnalysedCount], () => `${data.BrokenLinks.LinksBrokenCount}/${data.BrokenLinks.LinksAnalysedCount} links broken`),
        status: this.getStatus([data.BrokenLinks?.LinksBrokenCount, data.BrokenLinks?.LinksAnalysedCount], () => data.BrokenLinks.LinksBrokenCount === 0 ? Statuses.Pass : Statuses.Fail),
        additionalInformationKey: this.getAdditionalInformationKey([data.BrokenLinks?.LinksBrokenCount, data.BrokenLinks?.LinksAnalysedCount], () => data.BrokenLinks?.Links.length ?? 0 > 0 ? 'Broken Links' : null),
        links: data.BrokenLinks?.Links ? data.BrokenLinks.Links.map(x => ({
          page: x.Page,
          content: x.Content,
          href: x.Href,
          statusCode: x.StatusCode})) : [],
      },
      contentRecentlyUpdated: {
        title: 'Content',
        content: this.getContent([data.AIReadiness?.ContentRecentlyUpdated], () => data.AIReadiness?.ContentRecentlyUpdated ? 'Recently Updated' : 'Not Recently Updated'),
        status: this.getStatus([data.AIReadiness?.ContentRecentlyUpdated], () => data.AIReadiness?.ContentRecentlyUpdated ? Statuses.Pass : Statuses.Warning)
      },
      faqStructuredDataFound: {
        title: 'FAQ Structured Data',
        content: this.getContent([data.AIReadiness?.FAQStructuredDataFound], () => data.AIReadiness?.FAQStructuredDataFound ? 'Found' : 'Not Found'),
        status: this.getStatus([data.AIReadiness?.FAQStructuredDataFound], () => data.AIReadiness?.FAQStructuredDataFound ? Statuses.Pass : Statuses.Warning)
      },
      headings: {
        title: 'Headings',
        content: this.getContent([data.Headings?.HeadingsCount, data.Headings?.BadHeadingsCount], () => `${data.Headings.HeadingsCount - data.Headings.BadHeadingsCount}/${data.Headings.HeadingsCount} headings optimized`),
        status: this.getStatus([data.Headings?.HeadingsCount, data.Headings?.BadHeadingsCount], () => {
          const percentBadHeadings = data.Headings.HeadingsCount === 0 ? 1 : data.Headings.BadHeadingsCount / data.Headings.HeadingsCount;
          if (percentBadHeadings > 0 && percentBadHeadings < 0.5) {
            return Statuses.Warning;
          } else if (percentBadHeadings >= 0.5) {
            return Statuses.Fail;
          } else if (percentBadHeadings === 0) {
            return Statuses.Pass;
          }
        })
      },
      indexedOnGoogle: {
        title: 'Indexed on Google',
        content: this.getContent([data.Indexability?.EstimatedIndexedPages], () => data.Indexability?.EstimatedIndexedPages > 0 ? 'Indexed' : 'Not Indexed'),
        status: this.getStatus([data.Indexability?.EstimatedIndexedPages], () => data.Indexability?.EstimatedIndexedPages ? Statuses.Pass : Statuses.Fail)
      },
      indexedPages: {
        title: 'Indexed Pages',
        content: this.getContent([data.Indexability?.EstimatedIndexedPages], () => `${data.Indexability.EstimatedIndexedPages} pages indexed`),
        status: this.getStatus([data.Indexability?.EstimatedIndexedPages], () => Statuses.Info)
      },
      llmsTxtFound: {
        title: 'llms.txt',
        content: this.getContent([data.AIReadiness?.LlmsTxtFound], () => data.AIReadiness?.LlmsTxtFound ? 'Found' : 'Not Found'),
        status: this.getStatus([data.AIReadiness?.LlmsTxtFound], () => data.AIReadiness?.LlmsTxtFound ? Statuses.Pass : Statuses.Fail)
      },
      localStructuredDataFound: {
        title: 'Local Structured Data',
        content: this.getContent([data.AIReadiness?.LocalStructuredDataFound], () => data.AIReadiness?.LocalStructuredDataFound ? 'Found' : 'Not Found'),
        status: this.getStatus([data.AIReadiness?.LocalStructuredDataFound], () => data.AIReadiness?.LocalStructuredDataFound ? Statuses.Pass : Statuses.Warning)
      },
      mobile: {
        title: 'Mobile Friendly',
        content: this.getContent([data.Mobile?.IsMobile, data.Mobile?.HasSmallText, data.Mobile?.HasSmallLinks, data.Mobile?.HasPagesWithFlash, data.Mobile?.HasHorizontalScroll],
          () => !data.Mobile.IsMobile
          || data.Mobile.HasSmallText
          || data.Mobile.HasSmallLinks
          || data.Mobile.HasPagesWithFlash
          || data.Mobile.HasHorizontalScroll ? 'Mobile issues' : 'Optimized for mobile'),
        status: this.getStatus([data.Mobile?.IsMobile, data.Mobile?.HasSmallText, data.Mobile?.HasSmallLinks, data.Mobile?.HasPagesWithFlash, data.Mobile?.HasHorizontalScroll],
          () => !data.Mobile.IsMobile
          || data.Mobile.HasSmallText
          || data.Mobile.HasSmallLinks
          || data.Mobile.HasPagesWithFlash
          || data.Mobile.HasHorizontalScroll ? Statuses.Fail : Statuses.Pass)
      },
      readingDifficultyOptimized: {
        title: 'Reading Difficulty',
        content: this.getContent([data.AIReadiness?.ReadingDifficultyOptimized], () => data.AIReadiness?.ReadingDifficultyOptimized ? 'Optimized' : 'Not Optimized'),
        status: this.getStatus([data.AIReadiness?.ReadingDifficultyOptimized], () => data.AIReadiness?.ReadingDifficultyOptimized ? Statuses.Pass : Statuses.Warning)
      },
      sitemap: {
        title: 'Sitemap',
        content: this.getContent([data.Sitemap?.HasSitemap, data.Sitemap?.SitemapIssues], () => !data.Sitemap.HasSitemap ? 'No sitemap found' : data.Sitemap.SitemapIssues ? 'Sitemap issues' : 'Valid sitemap'),
        status: this.getStatus([data.Sitemap?.HasSitemap, data.Sitemap?.SitemapIssues], () => !data.Sitemap.HasSitemap ? Statuses.Fail : data.Sitemap.SitemapIssues ? Statuses.Warning : Statuses.Pass)
      },
      sslEncryption: {
        title: 'SSL Encryption',
        content: this.getContent([data.ServerBehaviour?.HasSsl], () => data.ServerBehaviour.HasSsl ? 'SSL Encryption is enabled' : 'SSL Encryption is not enabled'),
        status: this.getStatus([data.ServerBehaviour?.HasSsl], () => data.ServerBehaviour.HasSsl ? Statuses.Pass : Statuses.Fail)
      },
      titlesAndDescriptions: {
        title: 'Titles and Descriptions',
        content: this.getContent([data.PageTitlesAndDescriptions?.MissingItems, data.PageTitlesAndDescriptions?.DuplicatedItems], () => data.PageTitlesAndDescriptions.MissingItems || data.PageTitlesAndDescriptions.DuplicatedItems ? 'Missing or duplicated items' : 'All items present and unique'),
        status: this.getStatus([data.PageTitlesAndDescriptions?.MissingItems, data.PageTitlesAndDescriptions?.DuplicatedItems], () => data.PageTitlesAndDescriptions.MissingItems || data.PageTitlesAndDescriptions.DuplicatedItems ? Statuses.Fail : Statuses.Pass)
      },
      websiteSize: {
        title: 'Website Size',
        content: this.getContent([data.AmountOfContent?.PagesFound], () => `${data.AmountOfContent.PagesFound} pages found`),
        status: this.getStatus([data.AmountOfContent?.PagesFound], () => Statuses.Info)
      }
    };
  }

  private getContent(properties: any[], content: () => string): string {
    return properties.length === 0 || properties.every(property => property !== undefined && property !== null)
      ? content()
      : 'No Data';
  }

  private getStatus(properties: any[], status: () => Statuses): Statuses {
    return properties.length === 0 || properties.every(property => property !== undefined && property !== null)
      ? status()
      : Statuses.Warning;
  }

  private getAdditionalInformationKey(properties: any[], key: () => string): string {
    return properties.length === 0 || properties.every(property => property !== undefined && property !== null)
      ? key()
      : null;
  }
}